import { useParams, Link } from 'react-router-dom';
import { articles, ArticleSection } from '@/mocks/articles';
import { useState } from 'react';

function SectionRenderer({ section, index }: { section: ArticleSection; index: number }) {
  const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});

  const toggleFaq = (i: number) => {
    setOpenFaqs((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  switch (section.type) {
    case 'text':
      return (
        <div className="space-y-5 text-foreground-600 leading-relaxed">
          {section.paragraphs?.map((p, i) => (
            <p key={i} className={i === 0 ? 'text-lg text-foreground-700' : 'text-base'} dangerouslySetInnerHTML={{ __html: p }} />
          ))}
        </div>
      );

    case 'toc':
      return (
        <div className="bg-background-100 rounded-2xl p-6 my-10 border border-background-200/50">
          <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">Table of Contents</h2>
          <ul className="space-y-2">
            {section.tocItems?.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-primary-500 font-semibold mt-0.5">{i + 1}.</span>
                <a href={`#section-${i + 1}`} className="text-foreground-600 hover:text-primary-500 transition-colors">
                  {item}
                </a>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'heading':
      return (
        <section id={section.id}>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-12 mb-4">
            {section.heading}
          </h2>
          {section.paragraphs?.map((p, i) => (
            <p key={i} className="text-base text-foreground-600 leading-relaxed mt-4" dangerouslySetInnerHTML={{ __html: p }} />
          ))}
          {section.items && section.listStyle === 'number' && (
            <div className="space-y-4 mt-4">
              {section.items.map((item, i) => {
                const colonIndex = item.indexOf(':');
                const title = colonIndex > -1 ? item.slice(0, colonIndex) : `Step ${i + 1}`;
                const desc = colonIndex > -1 ? item.slice(colonIndex + 1).trim() : item;
                return (
                  <div key={i} className="flex gap-4 p-4 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 text-sm">{title}</h3>
                      <p className="text-sm text-foreground-500 mt-1">{desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {section.items && section.listStyle === 'check' && (
            <ul className="space-y-3 mt-4 list-none">
              {section.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                  <span className="text-foreground-600">{item}</span>
                </li>
              ))}
            </ul>
          )}
          {section.tableHeaders && section.tableRows && (
            <div className="overflow-x-auto mt-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-200">
                    {section.tableHeaders.map((h, i) => (
                      <th key={i} className="text-left py-3 px-3 text-sm font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.tableRows.map((row, ri) => (
                    <tr key={ri} className="border-b border-background-200/50">
                      {row.cells.map((cell, ci) => (
                        <td key={ci} className={`py-3 px-3 text-sm ${ci === 0 ? 'font-semibold text-foreground-900 whitespace-nowrap' : 'text-foreground-600'}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      );

    case 'cta':
      return (
        <div className="my-10 bg-primary-50 rounded-2xl p-8 text-center border border-primary-100">
          <h3 className="font-heading text-xl font-bold text-primary-800">
            {section.ctaTitle}
          </h3>
          <p className="mt-2 text-sm text-primary-600">
            {section.ctaText}
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-primary-600 border-2 border-primary-300 hover:bg-primary-100 rounded-full transition-colors whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      );

    case 'faq':
      return (
        <section id={section.id}>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 mt-12 mb-6">
            {section.heading}
          </h2>
          <div className="space-y-3">
            {section.faqs?.map((faq, i) => (
              <div key={i} className="bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                  <i className={`text-foreground-400 shrink-0 transition-transform duration-200 ${openFaqs[i] ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                </button>
                {openFaqs[i] && (
                  <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-4 text-foreground-500">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full transition-colors whitespace-nowrap">
            Back to Homepage
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.description,
            "url": `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`,
            "dateModified": "2026-07-09",
            "datePublished": "2026-07-09",
            "author": {
              "@type": "Organization",
              "name": "KakobuyFinds",
              "url": import.meta.env.VITE_SITE_URL,
            },
            "publisher": {
              "@type": "Organization",
              "name": "KakobuyFinds",
              "url": import.meta.env.VITE_SITE_URL,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
              },
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`,
            },
          }),
        }}
      />
      {/* Article Header */}
      <section className="bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{article.breadcrumbLabel}</span>
            </div>

            {/* Category tag */}
            <span className="inline-flex px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              {article.category}
            </span>

            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base text-foreground-500 leading-relaxed">
              {article.description}
            </p>

            {/* Meta */}
            <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
              <span>Updated: {article.updatedDate}</span>
              <span>&bull;</span>
              <span>{article.readTime}</span>
              <span>&bull;</span>
              <span>Kakobuy Spreadsheet</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
          <div className="max-w-3xl mx-auto">
            <div className="prose-custom space-y-6">
              {article.sections.map((section, idx) => (
                <SectionRenderer key={idx} section={section} index={idx} />
              ))}
            </div>

            {/* Internal Links */}
            <div className="mt-16 pt-10 border-t border-background-200">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Continue Reading</h3>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 hover:bg-primary-50 hover:text-primary-600 rounded-full transition-colors"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Homepage
                </Link>
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 3)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 hover:bg-primary-50 hover:text-primary-600 rounded-full transition-colors"
                    >
                      <i className="ri-article-line"></i>
                      {a.category}
                    </Link>
                  ))}
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 hover:bg-primary-50 hover:text-primary-600 rounded-full transition-colors"
                >
                  <i className="ri-external-link-line"></i>
                  Kakobuy Sheet
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}